import React, { type ButtonHTMLAttributes, type AnchorHTMLAttributes } from "react";

import type { SerializedStyles } from "@emotion/react";

import ButtonLoader from "./Button.Loader";

import * as styles from "./Button.styles";

type ThemesType = "primary" | "gray" | "orange" | "white";
type SizeType = "small" | "mini" | "twoLine";
type IconPositionType = "left" | "right";

interface ButtonBaseProps {
  iconPosition?: IconPositionType;
  content?: string | React.ReactNode;
  theme?: ThemesType;
  icon?: React.ReactNode;
  fluid?: boolean;
  unstyled?: boolean;
  loading?: boolean;
  size?: SizeType;
  contentStyles?: SerializedStyles;
  outline?: boolean;
}

export interface ButtonAsButton extends ButtonBaseProps, Omit<ButtonHTMLAttributes<HTMLButtonElement>, "content"> {
  as?: "button" | undefined;
}

interface ButtonAsAnchor extends ButtonBaseProps, Omit<AnchorHTMLAttributes<HTMLAnchorElement>, "content"> {
  as: "a";
}

interface ButtonAsComponent extends ButtonBaseProps {
  as: React.ComponentType<any> | string;
  [key: string]: any;
}

type ButtonProps = ButtonAsButton | ButtonAsAnchor | ButtonAsComponent;


function Button({
  as = "button",
  content,
  children,
  fluid = false,
  loading = false,
  contentStyles,
  theme = "primary",
  size,
  unstyled = false,
  style,
  outline = false,
  icon,
  iconPosition = "left",
  ...otherProps
}: ButtonProps) {

  const getThemeStyle = (currentTheme: ThemesType) => {
    const stylesObj = outline ? styles.outlines : styles.themes;
    return (stylesObj as Record<ThemesType, SerializedStyles>)[currentTheme];
  };

  const getSizeStyle = (currentSize: SizeType) => {
    return (styles.sizes as Record<SizeType, SerializedStyles>)[currentSize];
  };

  const getIconStyle = (position: IconPositionType) => {
    return (styles.withIcon as Record<IconPositionType, SerializedStyles>)[position];
  };

  const stylesMerged = unstyled ?
    [ styles.unstyled, style ] :
    [
      styles.button,
      getThemeStyle(theme),
      loading && styles.loading,
      fluid && styles.fluid,
      size && getSizeStyle(size),
      icon && getIconStyle(iconPosition),
      style
    ].filter(Boolean);

  const Tag = as as any;

  const buttonProps = as === "button" || as === undefined ?
    {
      type: "button" as const,
      ...otherProps
    } :
    otherProps;

  return (
    <Tag css={stylesMerged} {...buttonProps}>

      {iconPosition === "left" && icon}
      <span
        className="btn-content"
        css={[ styles.content, contentStyles ]}>
        {children || content}
      </span>
      {iconPosition === "right" && icon}

      <ButtonLoader loading={loading} />
    </Tag>
  );
}

export default Button;
